import { taxComplianceApiBaseUrl, taxFormsApiBaseUrl } from '../../shared/env';
import { createJsonClient } from '../../shared/httpClient';
import type { TaxFilingsGateway } from './TaxFilingsGateway';
import type {
  AnnualFormResponse,
  QuarterlyFormResponse,
  TaxFilingDashboardCardResponse,
  TaxFilingPlatformSummaryResponse,
  TaxFilingResponse,
} from './types';

export class HttpTaxFilingsGateway implements TaxFilingsGateway {
  private readonly taxComplianceRequest = createJsonClient(taxComplianceApiBaseUrl);
  private readonly taxFormsRequest = createJsonClient(taxFormsApiBaseUrl);

  listLiabilities(signal?: AbortSignal): Promise<{ id: string; formCode: number; taxYear: number; quarter: number | null }[]> {
    return this.taxComplianceRequest('/api/tax-liabilities', undefined, signal);
  }

  calculateLiability(input: { formCode: number; taxYear: number; quarter: number | null }): Promise<{ id: string; formCode: number; taxYear: number; quarter: number | null }> {
    return this.taxComplianceRequest('/api/tax-liabilities', { method: 'POST', body: JSON.stringify(input) });
  }

  listDashboard(taxYear: number, companyId?: string, signal?: AbortSignal): Promise<TaxFilingDashboardCardResponse[]> {
    const query = companyId ? `taxYear=${taxYear}&companyId=${companyId}` : `taxYear=${taxYear}`;
    return this.taxComplianceRequest(`/api/tax-filings-dashboard?${query}`, undefined, signal);
  }

  listPlatformSummary(taxYear: number, signal?: AbortSignal): Promise<TaxFilingPlatformSummaryResponse[]> {
    return this.taxComplianceRequest(`/api/tax-filings-dashboard/platform-summary?taxYear=${taxYear}`, undefined, signal);
  }

  listFilings(signal?: AbortSignal): Promise<TaxFilingResponse[]> {
    return this.taxComplianceRequest('/api/tax-filings', undefined, signal);
  }

  submitFiling(input: { formCode: number; taxYear: number; quarter: number | null }): Promise<TaxFilingResponse> {
    return this.taxComplianceRequest('/api/tax-filings', { method: 'POST', body: JSON.stringify(input) });
  }

  approveFiling(id: string): Promise<TaxFilingResponse> {
    return this.taxComplianceRequest(`/api/tax-filings/${id}/approve`, { method: 'POST' });
  }

  denyFiling(id: string, reason: string): Promise<TaxFilingResponse> {
    return this.taxComplianceRequest(`/api/tax-filings/${id}/deny`, { method: 'POST', body: JSON.stringify({ reason }) });
  }

  listQuarterlyForms(signal?: AbortSignal): Promise<QuarterlyFormResponse[]> {
    return this.taxFormsRequest('/api/quarterly-forms', undefined, signal);
  }

  generateQuarterlyForm(taxLiabilityId: string, taxFilingId: string | null): Promise<QuarterlyFormResponse> {
    return this.taxFormsRequest(`/api/tax-liabilities/${taxLiabilityId}/quarterly-forms`, { method: 'POST', body: JSON.stringify({ taxFilingId }) });
  }

  listAnnualForms(signal?: AbortSignal): Promise<AnnualFormResponse[]> {
    return this.taxFormsRequest('/api/annual-forms', undefined, signal);
  }

  generateAnnualForm(taxLiabilityId: string, taxFilingId: string | null): Promise<AnnualFormResponse> {
    return this.taxFormsRequest(`/api/tax-liabilities/${taxLiabilityId}/annual-forms`, { method: 'POST', body: JSON.stringify({ taxFilingId }) });
  }
}
